import { CartAddEvent } from '@theme/events';

/**
 * <product-bundle>
 *
 * A "frequently bought together" widget. Add-on products are either rendered by
 * Liquid (manual / metafield sources) or fetched client-side from Shopify's
 * product recommendations (automatic source). Lets the shopper toggle items and
 * pick variants, keeps a running total, and adds the whole selection to the cart
 * in one /cart/add request — then opens the cart drawer.
 */
class ProductBundle extends HTMLElement {
  async connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.discountPercent = parseFloat(this.dataset.discountPercent || '0') || 0;
    this.list = this.querySelector('[data-bundle-list]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.wasEl = this.querySelector('[data-bundle-was]');
    this.savingsAmountEl = this.querySelector('[data-bundle-savings-amount]');
    this.addButton = this.querySelector('[data-bundle-add]');
    this.errorEl = this.querySelector('[data-bundle-error]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('.bundle__check, .bundle__variant')) this.recompute();
    });

    if (this.addButton) {
      this.addButton.addEventListener('click', () => this.addBundle());
    }

    if (this.dataset.autoIntent) {
      await this.#loadRecommendations();
    } else {
      this.recompute();
    }
  }

  /** Live list of item rows (includes any appended via recommendations). */
  get items() {
    return Array.from(this.querySelectorAll('[data-bundle-item]'));
  }

  /** @returns {{ item: HTMLElement, id: string, price: number }[]} */
  getSelected() {
    const selected = [];
    for (const item of this.items) {
      const check = item.querySelector('.bundle__check');
      if (check && !check.checked) continue;

      const variantEl = item.querySelector('[data-bundle-variant]');
      if (!variantEl) continue;

      let id;
      let price;
      if (variantEl.tagName === 'SELECT') {
        const opt = variantEl.selectedOptions[0];
        if (!opt || opt.disabled) continue;
        id = opt.value;
        price = parseInt(opt.dataset.price, 10) || 0;
      } else {
        id = variantEl.value;
        price = parseInt(variantEl.dataset.price, 10) || 0;
      }
      if (!id) continue;
      selected.push({ item, id, price });
    }
    return selected;
  }

  recompute() {
    const selected = this.getSelected();
    const subtotal = selected.reduce((sum, s) => sum + s.price, 0);
    const discounted =
      this.discountPercent > 0 ? Math.round(subtotal * (1 - this.discountPercent / 100)) : subtotal;

    if (this.totalEl) this.totalEl.textContent = formatMoney(discounted, this.moneyFormat);
    if (this.wasEl) this.wasEl.textContent = formatMoney(subtotal, this.moneyFormat);
    if (this.savingsAmountEl) {
      this.savingsAmountEl.textContent = formatMoney(subtotal - discounted, this.moneyFormat);
    }

    if (this.addButton) this.addButton.disabled = selected.length === 0;
  }

  // ── Automatic source: fetch Shopify recommendations and build item rows ──────

  async #loadRecommendations() {
    const base = this.dataset.recommendationsUrl || '/recommendations/products';
    const intent = this.dataset.autoIntent || 'complementary';
    const limit = parseInt(this.dataset.autoLimit || '3', 10) || 3;
    const currentId = String(this.dataset.currentId || '');

    let products = await this.#fetchRecs(base, intent, limit + 2, currentId);

    // Complementary may be empty if none are set in Search & Discovery — fall back to related.
    if (products.length === 0 && this.dataset.autoFallback === 'true' && intent !== 'related') {
      products = await this.#fetchRecs(base, 'related', limit + 2, currentId);
    }

    products = products.slice(0, limit);

    // A bundle needs at least one add-on; otherwise remove the widget entirely.
    if (products.length === 0) {
      this.remove();
      return;
    }

    for (const product of products) {
      if (this.list.querySelector('[data-bundle-item]')) {
        const plus = document.createElement('div');
        plus.className = 'bundle__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        this.list.appendChild(plus);
      }
      this.list.appendChild(this.#buildItem(product));
    }

    this.classList.remove('bundle--loading');
    this.recompute();
  }

  async #fetchRecs(base, intent, limit, currentId) {
    try {
      const url = `${base}.json?intent=${encodeURIComponent(intent)}&product_id=${encodeURIComponent(
        currentId
      )}&limit=${limit}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      const items = Array.isArray(data.products) ? data.products : [];
      return items.filter((p) => String(p.id) !== currentId);
    } catch (error) {
      return [];
    }
  }

  /**
   * Builds a bundle item row from a recommendations product object.
   * Mirrors the markup of snippets/bundle-item.liquid.
   * @param {Record<string, any>} product
   * @returns {HTMLElement}
   */
  #buildItem(product) {
    const item = document.createElement('div');
    item.className = 'bundle__item';
    item.setAttribute('data-bundle-item', '');
    if (product.available === false) item.classList.add('bundle__item--soldout');

    const label = document.createElement('label');
    label.className = 'bundle__check-label';
    const check = document.createElement('input');
    check.type = 'checkbox';
    check.className = 'bundle__check';
    check.checked = product.available !== false;
    if (product.available === false) check.disabled = true;
    check.setAttribute('aria-label', product.title || '');
    label.appendChild(check);
    item.appendChild(label);

    const media = document.createElement('a');
    media.className = 'bundle__media';
    media.href = product.url || '#';
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'bundle__image';
      img.loading = 'lazy';
      img.alt = '';
      img.src = sizedImage(product.featured_image, 160);
      media.appendChild(img);
    }
    item.appendChild(media);

    const info = document.createElement('div');
    info.className = 'bundle__info';

    const title = document.createElement('a');
    title.className = 'bundle__title';
    title.href = product.url || '#';
    title.textContent = product.title || '';
    info.appendChild(title);

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstAvailable = variants.find((v) => v.available) || variants[0];

    if (variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'bundle__variant';
      select.setAttribute('data-bundle-variant', '');
      select.setAttribute('aria-label', `Choose an option for ${product.title || ''}`);
      for (const variant of variants) {
        const opt = document.createElement('option');
        opt.value = variant.id;
        opt.dataset.price = variant.price;
        if (!variant.available) opt.disabled = true;
        if (firstAvailable && variant.id === firstAvailable.id) opt.selected = true;
        opt.textContent = `${variant.title} — ${formatMoney(variant.price, this.moneyFormat)}`;
        select.appendChild(opt);
      }
      info.appendChild(select);
    } else if (firstAvailable) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.setAttribute('data-bundle-variant', '');
      hidden.value = firstAvailable.id;
      hidden.dataset.price = firstAvailable.price;
      info.appendChild(hidden);

      const price = document.createElement('span');
      price.className = 'bundle__price';
      price.textContent = formatMoney(firstAvailable.price, this.moneyFormat);
      info.appendChild(price);
    }

    item.appendChild(info);
    return item;
  }

  // ── Add to cart ──────────────────────────────────────────────────────────────

  async addBundle() {
    const selected = this.getSelected();
    if (selected.length === 0) return;

    this.#clearError();
    this.#setLoading(true);

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const body = {
      items: selected.map((s) => ({ id: Number(s.id), quantity: 1 })),
    };
    if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

    const cartAddUrl =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#showError(data.description || data.message || 'Could not add the bundle to your cart.');
        return;
      }

      let sections = data.sections;

      // Click-gated discount: apply the bundle code only when the button is used.
      const code = (this.dataset.discountCode || '').trim();
      if (code) {
        const applied = await this.#applyDiscount(code, [...new Set(sectionIds)]);
        if (applied && applied.sections) sections = applied.sections;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'product-bundle', {
          source: 'product-bundle',
          sections,
        })
      );

      this.#openCart();
    } catch (error) {
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * Applies the bundle discount code via the Cart AJAX API ({@link https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js}),
   * merging with any codes already on the cart. Returns refreshed section HTML.
   * @param {string} code
   * @param {string[]} sectionIds
   * @returns {Promise<{sections: Record<string,string>}|null>}
   */
  async #applyDiscount(code, sectionIds) {
    const url = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js';

    // Keep any discount codes already applied to the cart.
    const existing = Array.from(document.querySelectorAll('.cart-discount__pill'))
      .map((pill) => pill.dataset.discountCode)
      .filter(Boolean);
    const codes = existing.includes(code) ? existing : [...existing, code];

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: sectionIds.join(',') }),
      });
      const data = await res.json();

      const rejected = (data.discount_codes || []).find((d) => d.code === code && d.applicable === false);
      if (rejected) {
        this.#showError("The bundle discount couldn't be applied to these items.");
      }

      return { sections: data.sections };
    } catch (error) {
      // Items are already in the cart; just skip the discount silently on network error.
      return null;
    }
  }

  #openCart() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      const cartUrl = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) || '/cart';
      window.location.href = cartUrl;
    }
  }

  #setLoading(isLoading) {
    if (!this.addButton) return;
    this.addButton.toggleAttribute('aria-busy', isLoading);
    this.addButton.disabled = isLoading;
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  #clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.classList.add('hidden');
  }
}

/**
 * Appends a width param to a Shopify CDN image URL.
 * @param {string} url
 * @param {number} width
 * @returns {string}
 */
function sizedImage(url, width) {
  if (!url) return url;
  return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
}

/**
 * Minimal Shopify money formatter (mirrors the common money_format tokens).
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  const match = (format || '').match(placeholderRegex);
  const token = match ? match[1] : 'amount';
  let value;

  switch (token) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', '');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return (format || '${{amount}}').replace(placeholderRegex, value);
}

if (!customElements.get('product-bundle')) {
  customElements.define('product-bundle', ProductBundle);
}
